import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutGrid, BookOpen, Users, KeyRound, LogOut, ExternalLink } from "lucide-react";
import { useAdminAuth, useNoIndex } from "@/admin/AdminAuth";

export const DEEP_PETROL = "#1F3A3F";

const NAV = [
  { to: "/admin", label: "Áttekintés", icon: LayoutGrid, end: true, id: "dashboard" },
  { to: "/admin/programok", label: "Programok és időpontok", icon: BookOpen, id: "programs" },
  { to: "/admin/jelentkezok", label: "Jelentkezők", icon: Users, id: "registrations" },
  { to: "/admin/beallitasok", label: "Jelszó módosítása", icon: KeyRound, id: "settings" },
];

function SideNav({ onLogout, email }) {
  return (
    <aside className="md:w-[270px] md:min-h-screen md:fixed md:inset-y-0 md:left-0 flex md:flex-col" style={{ background: DEEP_PETROL }}>
      <div className="hidden md:block px-8 pt-10 pb-8 border-b border-white/10">
        <p className="font-serif-display text-[24px] text-[#F4EEE2] leading-tight">
          Szász-Tóth <em className="text-[#D9BE85]" style={{ fontStyle: "italic" }}>Enikő</em>
        </p>
        <p className="mt-2 font-body text-[10.5px] tracking-[0.32em] uppercase text-[#D9BE85]/80">Adminfelület</p>
      </div>
      <nav className="flex flex-wrap md:flex-nowrap md:flex-col px-3 md:px-4 py-3 md:py-6 gap-1 flex-1">
        {NAV.map(({ to, label, icon: Icon, end, id }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            data-testid={`admin-nav-${id}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-2.5 md:py-3 whitespace-nowrap font-body text-[13px] md:text-[14px] transition-colors duration-200 border-l-2 ${
                isActive ? "bg-white/[0.07] text-[#F4EEE2] border-[#D9BE85]" : "text-[#F4EEE2]/65 border-transparent hover:text-[#F4EEE2] hover:bg-white/[0.04]"
              }`
            }
          >
            <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
            {label}
          </NavLink>
        ))}
      </nav>
      <div className="hidden md:block px-8 py-6 border-t border-white/10 space-y-3">
        <p className="font-body text-[12px] text-[#F4EEE2]/55 truncate" data-testid="admin-user-email">{email}</p>
        <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-2 font-body text-[13px] text-[#F4EEE2]/70 hover:text-[#F4EEE2]">
          <ExternalLink className="w-3.5 h-3.5" /> Honlap megnyitása
        </a>
        <button type="button" onClick={onLogout} data-testid="admin-logout-btn" className="flex items-center gap-2 font-body text-[13px] text-[#D9BE85] hover:text-[#F4EEE2] transition-colors">
          <LogOut className="w-3.5 h-3.5" /> Kijelentkezés
        </button>
      </div>
    </aside>
  );
}

export default function AdminLayout() {
  useNoIndex();
  const { user, logout } = useAdminAuth();
  const navigate = useNavigate();
  const handleLogout = async () => {
    await logout();
    navigate("/admin/belepes", { replace: true });
  };
  return (
    <div data-testid="admin-layout" className="min-h-screen bg-warm text-graphite">
      <SideNav onLogout={handleLogout} email={user?.email} />
      <div className="md:hidden flex items-center justify-between px-5 py-3 bg-tinted border-b border-hairline">
        <p className="font-serif-display text-[18px]">Adminfelület</p>
        <button type="button" onClick={handleLogout} data-testid="admin-logout-btn-mobile" className="font-body text-[13px] text-gold">Kijelentkezés</button>
      </div>
      <main className="md:ml-[270px] px-5 md:px-12 py-8 md:py-12 max-w-[1180px]">
        <Outlet />
      </main>
    </div>
  );
}

export function PageHead({ eyebrow, title, children }) {
  return (
    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-8 md:mb-10">
      <div>
        {eyebrow && <p className="font-body text-[11px] tracking-[0.34em] uppercase text-gold mb-3">{eyebrow}</p>}
        <h1 data-testid="admin-page-title" className="font-serif-display text-[32px] md:text-[40px] leading-[1.1] text-graphite">{title}</h1>
      </div>
      {children && <div className="flex flex-wrap gap-3">{children}</div>}
    </div>
  );
}

export function NotReadyNote({ children, testId }) {
  return (
    <div data-testid={testId} className="border-l-2 border-gold bg-tinted px-5 py-3.5 font-body text-[13.5px] leading-[1.6] text-graphite-soft">
      {children}
    </div>
  );
}
